import { useCallback, useEffect, useRef, useState } from "react";
import {
  ADHOC_KYC_FRAGMENT_DISABLED,
  ADHOC_KYC_FRAGMENT_DONE,
  ADHOC_KYC_FRAGMENT_EXPIRED,
  ADHOC_KYC_FRAGMENT_REVOKED,
  ADHOC_KYC_FRAGMENT_TOKEN,
} from "src/store/features/api/adhocKyc";

/**
 * sessionStorage key for the raw magic-link token. Session (not local)
 * storage so it dies with the tab, and never the URL so it stays out of
 * history, analytics and server logs.
 */
export const ADHOC_KYC_TOKEN_STORAGE_KEY = "adhocKycToken";

/** How the customer arrived on `/adhocKyc`. */
export type AdhocKycEntryKind =
  | "token"
  | "expired"
  | "done"
  | "invalid"
  | "revoked"
  | "disabled"
  | "none";

export interface AdhocKycEntry {
  kind: AdhocKycEntryKind;
  /** Raw token — present for `token` (live) and `expired` (needed by resend). */
  token?: string;
}

/**
 * Decodes the fragment the backend's `/adhoc-kyc/open` bounce writes:
 * `#akt=<token>`, `#expired=<token>`, `#done`, `#invalid`, `#revoked`,
 * `#disabled`. Anything else non-empty is treated as `invalid`; an empty
 * fragment yields `null` so the caller can fall back to storage / session.
 */
export function parseAdhocKycFragment(hash: string): AdhocKycEntry | null {
  const raw = hash.replace(/^#/, "");
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const token = params.get(ADHOC_KYC_FRAGMENT_TOKEN);
  if (token) return { kind: "token", token };
  const expired = params.get(ADHOC_KYC_FRAGMENT_EXPIRED);
  if (expired) return { kind: "expired", token: expired };
  if (params.has(ADHOC_KYC_FRAGMENT_DONE)) return { kind: "done" };
  if (params.has(ADHOC_KYC_FRAGMENT_REVOKED)) return { kind: "revoked" };
  if (params.has(ADHOC_KYC_FRAGMENT_DISABLED)) return { kind: "disabled" };
  return { kind: "invalid" };
}

// Storage can throw (private mode, disabled site data); the flow must still
// work for the current page load, so every access is guarded.
const readStoredToken = (): string | undefined => {
  try {
    return window.sessionStorage.getItem(ADHOC_KYC_TOKEN_STORAGE_KEY) || undefined;
  } catch {
    return undefined;
  }
};

export const forgetAdhocKycToken = (): void => {
  try {
    window.sessionStorage.removeItem(ADHOC_KYC_TOKEN_STORAGE_KEY);
  } catch {
    // nothing to clean up
  }
};

const storeToken = (token: string): void => {
  try {
    window.sessionStorage.setItem(ADHOC_KYC_TOKEN_STORAGE_KEY, token);
  } catch {
    // the in-memory copy still drives this page load
  }
};

/**
 * Reads the entry for this page load WITHOUT side effects: the fragment wins;
 * with no fragment a stored token resumes token mode; otherwise `none`
 * (session mode if logged in, invalid page if not). Pure on purpose — React
 * StrictMode invokes state initialisers twice in development, and a resolver
 * that stripped the fragment on the first call handed the second call an
 * empty hash, turning every `#expired=` / `#done` / `#revoked` / `#disabled`
 * bounce into the generic invalid page.
 */
export function peekAdhocKycEntry(): AdhocKycEntry {
  const fromFragment = parseAdhocKycFragment(window.location.hash);
  if (fromFragment) return fromFragment;
  const stored = readStoredToken();
  return stored ? { kind: "token", token: stored } : { kind: "none" };
}

/**
 * Applies the entry's side effects once: strips the fragment (`replaceState`,
 * so no history entry keeps the token), persists a live token for refreshes,
 * and clears a stale stored token for any other bounce. Idempotent.
 */
export function commitAdhocKycEntry(entry: AdhocKycEntry, opts: { store?: boolean } = {}): void {
  if (window.location.hash) {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
  if (entry.kind === "token" && entry.token) {
    if (opts.store !== false) storeToken(entry.token);
  } else if (entry.kind !== "none") {
    forgetAdhocKycToken();
  }
}

/** Peek + commit in one call — for callers outside React's render phase. */
export function resolveAdhocKycEntry(): AdhocKycEntry {
  const entry = peekAdhocKycEntry();
  commitAdhocKycEntry(entry);
  return entry;
}

export function useAdhocKycToken(): { entry: AdhocKycEntry; forgetToken: () => void } {
  // Pure read in the initialiser: React 17 StrictMode re-runs the whole render
  // (initialiser included) and keeps the second result, so both passes must
  // see the fragment. The strip/store commit is deferred to an effect. A
  // child's effect (the step router forgetting a dead token) runs BEFORE this
  // parent's, so the commit must not re-store a token already forgotten.
  const [entry] = useState<AdhocKycEntry>(peekAdhocKycEntry);
  const forgotten = useRef(false);
  useEffect(() => {
    commitAdhocKycEntry(entry, { store: !forgotten.current });
  }, [entry]);
  // Drops the stored copy once the server says the token is dead, so a
  // refresh doesn't keep replaying it. The current screen keeps its reason.
  const forgetToken = useCallback(() => {
    forgotten.current = true;
    forgetAdhocKycToken();
  }, []);
  return { entry, forgetToken };
}
